import { ActionButton, Button, Modal } from "@indigo-components";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useModalBlocking } from "~/hooks/useModalBlocking";

interface ConfirmationModalControl {
  setConfirmDisabled: (disabled: boolean) => void;
}

const ConfirmationModalControlContext =
  createContext<ConfirmationModalControl | null>(null);

export const useConfirmationModalControl = () =>
  useContext(ConfirmationModalControlContext);

interface ConfirmationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  title?: string;
  titleIcon?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  showSuccess?: boolean;
  confirmButtonText?: string;
  confirmButtonVariant?: "primary" | "danger";
  width?: number;
}

const ConfirmationModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  onCancel,
  title,
  titleIcon,
  subtitle,
  children,
  showSuccess = false,
  confirmButtonText = "Confirm",
  confirmButtonVariant = "primary",
  width = 389,
}: ConfirmationModalProps) => {
  const { modalProps, getBlockableOnCancel, startBlocking, stopBlocking } =
    useModalBlocking();
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const controlValue = useMemo<ConfirmationModalControl>(
    () => ({ setConfirmDisabled }),
    [],
  );

  const handleCancel = () => {
    setIsOpen(false);
    onCancel?.();
  };

  const handleConfirm = useCallback(async () => {
    startBlocking("confirm");
    try {
      await onConfirm();
      // Don't close modal here - let onSuccess callback handle it
    } catch (error) {
      console.error("Confirmation failed:", error);
      throw error; // Re-throw so ActionButton can handle error state
    } finally {
      stopBlocking("confirm");
    }
  }, [onConfirm, startBlocking, stopBlocking]);
  return (
    <Modal
      open={isOpen}
      onCancel={getBlockableOnCancel(handleCancel)}
      footer={null}
      centered
      width={width}
      className="flex flex-col justify-between p-6"
      closable={modalProps.closable}
      maskClosable={modalProps.maskClosable}
    >
      <div className="flex flex-col gap-4">
        {title && (
          <h3
            style={{
              color: "var(--indigo-text-primary)",
              fontSize: "16px",
              fontFamily: "var(--indigo-font-body)",
              margin: 0,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {titleIcon}
            {title}
          </h3>
        )}
        {subtitle && (
          <div
            style={{
              color: "var(--indigo-text-secondary)",
              fontSize: "14px",
              fontFamily: "var(--indigo-font-body)",
              lineHeight: "18px",
            }}
          >
            {subtitle}
          </div>
        )}
        <ConfirmationModalControlContext.Provider value={controlValue}>
          {children}
        </ConfirmationModalControlContext.Provider>
        <div className="flex justify-end gap-[10px]">
          <Button
            variant="ghost"
            size="md"
            onClick={handleCancel}
            disabled={!modalProps.closable}
          >
            Cancel
          </Button>
          <ActionButton
            variant={confirmButtonVariant}
            size="md"
            onClick={handleConfirm}
            text={confirmButtonText}
            showSuccess={showSuccess}
            disabled={confirmDisabled}
            onSuccess={() => {
              setIsOpen(false);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
